import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Download, X, Share } from 'lucide-react';

export function PorteroInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already installed as standalone
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) return;

    // Check if dismissed recently
    const dismissedAt = localStorage.getItem('portero_install_dismissed');
    if (dismissedAt && Date.now() - parseInt(dismissedAt) < 86400000) return; // 24h

    // Detect iOS
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isiOS);

    if (isiOS) {
      // iOS doesn't support beforeinstallprompt, show manual guide
      setShowBanner(true);
      return;
    }

    // Android/Desktop Chrome - capture install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Fallback: show banner after 3 seconds on mobile even without prompt
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    let timer;
    if (isMobile) {
      timer = setTimeout(() => setShowBanner(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    } else if (isIOS) {
      setShowIOSGuide(true);
    } else {
      // Fallback: show manual instructions
      setShowIOSGuide(true);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem('portero_install_dismissed', Date.now().toString());
  };

  if (!showBanner || dismissed) return null;

  return (
    <>
      {/* Install banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-3 animate-in slide-in-from-bottom duration-500">
        <div className="bg-zinc-800 border border-zinc-700 rounded-2xl p-4 flex items-center gap-3 shadow-2xl max-w-md mx-auto">
          <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0">
            <Download className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white">Instalar Portero GPS</p>
            <p className="text-xs text-zinc-400">Acceso directo desde tu pantalla</p>
          </div>
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
            onClick={handleInstall}
            data-testid="btn-instalar-pwa"
          >
            Instalar
          </Button>
          <button onClick={handleDismiss} className="text-zinc-500 hover:text-zinc-300 shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* iOS / Manual install guide */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-end sm:items-center justify-center p-4" onClick={() => setShowIOSGuide(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-sm w-full space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white">Instalar Portero GPS</h3>
            {isIOS ? (
              <div className="space-y-3 text-sm text-zinc-300">
                <div className="flex items-start gap-3">
                  <span className="bg-zinc-800 rounded-lg w-7 h-7 flex items-center justify-center shrink-0 text-xs font-bold text-emerald-400">1</span>
                  <p>Toca el bot&oacute;n <Share className="inline h-4 w-4 text-blue-400" /> <strong>Compartir</strong> en la barra de Safari</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-zinc-800 rounded-lg w-7 h-7 flex items-center justify-center shrink-0 text-xs font-bold text-emerald-400">2</span>
                  <p>Despl&aacute;zate y selecciona <strong>&quot;Agregar a pantalla de inicio&quot;</strong></p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-zinc-800 rounded-lg w-7 h-7 flex items-center justify-center shrink-0 text-xs font-bold text-emerald-400">3</span>
                  <p>Toca <strong>&quot;Agregar&quot;</strong> y listo</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-sm text-zinc-300">
                <p>Si ya tienes otra app instalada de este sitio:</p>
                <div className="flex items-start gap-3">
                  <span className="bg-zinc-800 rounded-lg w-7 h-7 flex items-center justify-center shrink-0 text-xs font-bold text-emerald-400">1</span>
                  <p>Toca los <strong>3 puntos</strong> del men&uacute; de Chrome</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-zinc-800 rounded-lg w-7 h-7 flex items-center justify-center shrink-0 text-xs font-bold text-emerald-400">2</span>
                  <p>Selecciona <strong>&quot;Agregar a pantalla de inicio&quot;</strong></p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-zinc-800 rounded-lg w-7 h-7 flex items-center justify-center shrink-0 text-xs font-bold text-emerald-400">3</span>
                  <p>Nombra la app <strong>&quot;Portero GPS&quot;</strong> y confirma</p>
                </div>
              </div>
            )}
            <Button className="w-full bg-zinc-800 hover:bg-zinc-700 text-white" onClick={() => setShowIOSGuide(false)}>
              Entendido
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
